import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useParams,
  useRouter,
} from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { getCookie, getRequest } from '@tanstack/react-start/server'
import { Calculator, FileText, Menu } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import z from 'zod'

import type { ChatCalculatorResult } from '@/routes/api/chat/-utils/status-interceptor'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { CalculatorsSidebar } from '@/features/ai/calculators-sidebar'
import { ChatBridgeProvider } from '@/features/ai/chat-bridge-context'
import {
  ChatLayoutContext,
  isRightPanelKind,
  type RightPanelKind,
} from '@/features/ai/chat-layout-context'
import { ChatSidebar } from '@/features/ai/chat-sidebar'
import {
  MIN_CHAT_WIDTH,
  RIGHT_PANEL_WIDTH,
  SIDEBAR_WIDTH,
} from '@/features/ai/constants'
import { RightPanelShell } from '@/features/ai/right-panel'
import { RightPanelBookmarks } from '@/features/ai/right-panel-bookmarks'
import { TemplatesPanel } from '@/features/templates/panel/templates-panel'
import { SettingsModal } from '@/features/user/settings-modal'
import { useIsMobile } from '@/hooks/use-mobile'
import { useTRPC } from '@/lib/trpc/react'
import { cn } from '@/lib/utils'

const COOKIE_KEYS = {
  leftWidth: 'chat-left-sidebar-width',
  rightWidth: 'chat-right-sidebar-width',
  leftSidebarState: 'left_sidebar_state',
  rightSidebarState: 'right_sidebar_state',
} as const

const PANEL_LABELS: Record<RightPanelKind, string> = {
  calculators: 'Калькуляторы и шкалы',
  templates: 'Шаблоны',
}

function parseCookieWidth(
  value: string | undefined,
  defaultValue: number,
  bounds: { min: number; max: number },
): number {
  if (!value) return defaultValue
  const parsed = Number.parseInt(value, 10)
  if (Number.isNaN(parsed)) return defaultValue
  return Math.max(bounds.min, Math.min(bounds.max, parsed))
}

function parseRightPanelState(value: string | undefined) {
  // Старое значение «true» означало открытые калькуляторы.
  if (value === 'true') return 'calculators'
  return isRightPanelKind(value) ? value : null
}

function setCookie(name: string, value: string, days = 365) {
  if (typeof document === 'undefined') return
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

const MOBILE_UA = /Mobi|Android|iPhone|iPad/i

const loader = createServerFn().handler(() => {
  const request = getRequest()
  const ua = request?.headers?.get('user-agent') ?? ''
  const isMobileFromServer = MOBILE_UA.test(ua)

  const leftSidebarState = getCookie(COOKIE_KEYS.leftSidebarState)
  const rightPanel = parseRightPanelState(
    getCookie(COOKIE_KEYS.rightSidebarState),
  )
  const leftWidth = parseCookieWidth(
    getCookie(COOKIE_KEYS.leftWidth),
    SIDEBAR_WIDTH.expanded,
    SIDEBAR_WIDTH,
  )
  const rightWidth = parseCookieWidth(
    getCookie(COOKIE_KEYS.rightWidth),
    RIGHT_PANEL_WIDTH.default,
    RIGHT_PANEL_WIDTH,
  )
  return {
    leftSidebarState,
    rightPanel,
    leftWidth,
    rightWidth,
    isMobileFromServer,
  }
})

export const Route = createFileRoute('/(require-auth)/(chat)')({
  component: Layout,
  loader: () => loader(),
  validateSearch: z.object({
    tab: z.enum(['payment']).optional(),
    panel: z.enum(['calculators', 'templates']).optional(),
  }),
})

function Layout() {
  const { user } = Route.useRouteContext()
  return (
    <ChatBridgeProvider key={user.id}>
      <LayoutContent />
    </ChatBridgeProvider>
  )
}

function LayoutContent() {
  const {
    rightPanel: initialRightPanel,
    leftSidebarState,
    leftWidth: initialLeftWidth,
    rightWidth: initialRightWidth,
    isMobileFromServer,
  } = Route.useLoaderData() || {}
  const search = Route.useSearch()
  const params = useParams({
    from: '/(require-auth)/(chat)/chat/$chatId',
    shouldThrow: false,
    select: (params) => ({
      chatId: params.chatId,
    }),
  })
  const isCollapsed = leftSidebarState !== 'true'
  const isMobile = useIsMobile(isMobileFromServer)
  const navigate = useNavigate()
  const router = useRouter()

  const trpc = useTRPC()
  const chatsQuery = useQuery(trpc.chat.getMyChats.queryOptions())
  const foldersQuery = useQuery(trpc.chat.getMyFolders.queryOptions())
  const createChat = useMutation(trpc.chat.createChat.mutationOptions())
  const { data: profile } = useQuery(trpc.user.getProfile.queryOptions())

  const [leftDrawerOpen, setLeftDrawerOpen] = useState(
    isMobile ? false : leftSidebarState !== 'true',
  )
  const [leftCollapsed, setLeftCollapsed] = useState(
    leftSidebarState === 'false',
  )
  // Правая панель закрыта по умолчанию; на мобильном не восстанавливается.
  const startPanel = isMobile ? null : (initialRightPanel ?? null)
  const [panel, setPanel] = useState<RightPanelKind | null>(startPanel)
  // Открытые однажды панели остаются смонтированными скрытыми, чтобы
  // наполовину заполненный документ не пропал при случайном закрытии.
  const [mountedPanels, setMountedPanels] = useState<RightPanelKind[]>(
    startPanel ? [startPanel] : [],
  )
  const lastPanel = useRef<RightPanelKind | null>(startPanel)
  const [rightResizing, setRightResizing] = useState(false)
  const [leftWidth, setLeftWidth] = useState(
    initialLeftWidth ?? SIDEBAR_WIDTH.expanded,
  )
  const [rightWidth, setRightWidth] = useState(
    initialRightWidth ?? RIGHT_PANEL_WIDTH.default,
  )
  const [settingsModalOpen, setSettingsModalOpen] = useState(false)
  const lastRestoredAssistantIdRef = useRef<string | null>(null)

  useEffect(() => {
    const currentChat = chatsQuery.data?.find(
      (chatItem) => chatItem.id === params?.chatId,
    )
    const activeAssistantMessageId =
      currentChat?.activeAssistantMessageId ?? null
    if (
      !activeAssistantMessageId ||
      lastRestoredAssistantIdRef.current === activeAssistantMessageId
    ) {
      return
    }

    lastRestoredAssistantIdRef.current = activeAssistantMessageId
    void router.invalidate()
  }, [chatsQuery.data, params?.chatId, router])

  useEffect(() => {
    setCookie(COOKIE_KEYS.leftWidth, leftWidth.toString())
  }, [leftWidth])

  useEffect(() => {
    setCookie(COOKIE_KEYS.rightWidth, rightWidth.toString())
  }, [rightWidth])

  useEffect(() => {
    setCookie(COOKIE_KEYS.leftSidebarState, String(!leftCollapsed))
  }, [leftCollapsed])

  useEffect(() => {
    if (isMobile) return
    setCookie(COOKIE_KEYS.rightSidebarState, panel ?? 'false')
  }, [panel, isMobile])

  useEffect(() => {
    if (isMobile) {
      setLeftDrawerOpen(false)
      setPanel(null)
    }
  }, [isMobile])

  // Окно сузили при открытой панели: сворачиваем левую колонку, чтобы чат
  // не сжался в полоску.
  useEffect(() => {
    if (isMobile || !panel) return
    const check = () => {
      const leftNow = leftCollapsed ? SIDEBAR_WIDTH.collapsed : leftWidth
      if (window.innerWidth - rightWidth - leftNow < MIN_CHAT_WIDTH) {
        setLeftCollapsed(true)
      }
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [isMobile, panel, leftCollapsed, leftWidth, rightWidth])

  const handleLeftResize = (delta: number) => {
    setLeftWidth((prev) => {
      const newWidth = prev + delta
      return Math.max(SIDEBAR_WIDTH.min, Math.min(SIDEBAR_WIDTH.max, newWidth))
    })
  }

  const handleRightResize = (delta: number) => {
    setRightWidth((prev) => {
      const newWidth = prev + delta
      return Math.max(
        RIGHT_PANEL_WIDTH.min,
        Math.min(RIGHT_PANEL_WIDTH.max, newWidth),
      )
    })
  }

  const [activeCalculatorId, setActiveCalculatorId] = useState<string | null>(
    null,
  )
  const [activeCalculatorResult, setActiveCalculatorResult] =
    useState<ChatCalculatorResult | null>(null)

  const openPanel = (kind: RightPanelKind) => {
    setMountedPanels((prev) => (prev.includes(kind) ? prev : [...prev, kind]))
    lastPanel.current = kind
    if (!isMobile && typeof window !== 'undefined') {
      // Панель сдвигает чат, а не накрывает его: если чату не хватит места,
      // сворачиваем левую колонку.
      const leftNow = leftCollapsed ? SIDEBAR_WIDTH.collapsed : leftWidth
      if (window.innerWidth - rightWidth - leftNow < MIN_CHAT_WIDTH) {
        setLeftCollapsed(true)
      }
    }
    setPanel(kind)
  }
  const closePanel = () => setPanel(null)
  const togglePanel = (kind: RightPanelKind) => {
    if (panel === kind) closePanel()
    else openPanel(kind)
  }

  const openCalculators = (
    calculatorId?: string,
    initialResult?: ChatCalculatorResult | null,
  ) => {
    if (calculatorId) {
      setActiveCalculatorId(calculatorId)
      setActiveCalculatorResult(initialResult ?? null)
    }
    openPanel('calculators')
  }

  const handleActiveCalculatorChange = (
    id: string | null,
    result?: ChatCalculatorResult | null,
  ) => {
    setActiveCalculatorId(id)
    setActiveCalculatorResult(result ?? null)
  }

  // Ссылка вида /chat?panel=templates открывает панель и очищает адрес.
  const requestedPanel = search.panel
  useEffect(() => {
    if (!requestedPanel) return
    openPanel(requestedPanel)
    void router.navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        panel: undefined,
      }),
      replace: true,
    })
    // openPanel читает актуальные ширины; повторный запуск не нужен.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedPanel])

  const sidebarContent = (
    <ChatSidebar
      isCollapsed={isMobile ? false : leftCollapsed}
      subscriptionPlan={profile?.subscriptionPlan}
      onToggle={() => {
        if (isMobile) {
          setLeftDrawerOpen(false)
        } else {
          setLeftCollapsed(!leftCollapsed)
        }
      }}
      width={leftWidth}
      onResize={handleLeftResize}
      chats={chatsQuery.data || []}
      folders={foldersQuery.data || []}
      foldersLoading={foldersQuery.isPending}
      foldersError={foldersQuery.isError}
      onRetryFolders={() => {
        foldersQuery.refetch()
      }}
      currentChatId={params?.chatId || 'new-chat-id'}
      onChatSelect={(id) => {
        navigate({ to: `/chat/${id}` })
        if (isMobile) {
          setLeftDrawerOpen(false)
        }
      }}
      onCreateNewChat={async (folderId) => {
        if (createChat.isPending) return
        try {
          if (folderId) {
            const created = await createChat.mutateAsync({
              folderId,
              title: 'Новый чат',
            })
            if (created)
              await navigate({
                to: '/chat/$chatId',
                params: { chatId: created.id },
              })
          } else {
            await navigate({ to: '/chat' })
            await router.invalidate()
          }
          if (isMobile) setLeftDrawerOpen(false)
        } catch (error) {
          toast.error('Не удалось создать чат', {
            description:
              error instanceof Error ? error.message : 'Попробуйте ещё раз',
          })
        }
      }}
      onProfileClick={() => {
        setSettingsModalOpen(true)
        if (isMobile) {
          setLeftDrawerOpen(false)
        }
      }}
      onSettingsClick={() => {
        setSettingsModalOpen(true)
        if (isMobile) {
          setLeftDrawerOpen(false)
        }
      }}
      isDrawer={isMobile}
    />
  )

  const panelContent = (kind: RightPanelKind, isDrawer: boolean) =>
    kind === 'calculators' ? (
      <CalculatorsSidebar
        activeCalculatorId={activeCalculatorId}
        activeCalculatorResult={activeCalculatorResult}
        onActiveCalculatorChange={handleActiveCalculatorChange}
        onClose={closePanel}
        isDrawer={isDrawer}
      />
    ) : (
      <TemplatesPanel onClose={closePanel} isDrawer={isDrawer} />
    )

  // Во время анимации закрытия показываем последнюю открытую панель.
  const shownPanel = panel ?? lastPanel.current

  const renderPanels = (isDrawer: boolean) =>
    mountedPanels.map((kind) => (
      <div
        key={kind}
        hidden={shownPanel !== kind}
        className="h-full min-h-0"
        data-right-panel={kind}
      >
        <RightPanelShell
          width={rightWidth}
          onResize={handleRightResize}
          onResizingChange={setRightResizing}
          isDrawer={isDrawer}
          label={PANEL_LABELS[kind]}
        >
          {panelContent(kind, isDrawer)}
        </RightPanelShell>
      </div>
    ))

  return (
    <ChatLayoutContext.Provider
      value={{
        rightPanel: panel,
        openPanel,
        closePanel,
        togglePanel,
        isCalculatorsOpen: panel === 'calculators',
        openCalculators,
        closeCalculators: closePanel,
        toggleCalculators: () => togglePanel('calculators'),
        openTemplates: () => openPanel('templates'),
      }}
    >
      {/* Страница не выше окна: панели и переписка скроллятся внутри себя,
            а сам документ не растёт. */}
      <SidebarProvider
        className="h-dvh overflow-hidden"
        defaultOpen={!isCollapsed}
      >
        {/* Desktop Left Sidebar */}
        {!isMobile && sidebarContent}

        {/* Mobile Header */}
        {isMobile && (
          <div className="fixed top-0 right-0 left-0 z-40 flex items-center justify-between border-b bg-background px-4 py-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLeftDrawerOpen(true)}
              type="button"
              aria-label="Открыть список чатов"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <img
                alt="Logo"
                className="size-6 invert dark:invert-0"
                src="/Logo-square.svg"
              />
              <span className="text-xl font-extrabold">IQDOC</span>
            </Link>
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openPanel('templates')}
                type="button"
                aria-label="Открыть шаблоны"
              >
                <FileText className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openPanel('calculators')}
                type="button"
                aria-label="Открыть калькуляторы"
              >
                <Calculator className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        <SidebarInset className="min-h-0 overflow-hidden">
          <Outlet />
          <RightPanelBookmarks />
        </SidebarInset>

        {/* Desktop Right Panel: стоит в потоке и сдвигает чат */}
        {!isMobile && (
          <div
            aria-hidden={!panel}
            className={cn(
              'relative h-full shrink-0 overflow-hidden',
              !rightResizing && 'transition-[width] duration-300 ease-out',
            )}
            style={{ width: panel ? rightWidth : 0 }}
          >
            <div
              className="absolute inset-y-0 right-0"
              style={{ width: rightWidth }}
            >
              {renderPanels(false)}
            </div>
          </div>
        )}

        {/* Mobile Drawers */}
        {isMobile && (
          <>
            <Sheet open={leftDrawerOpen} onOpenChange={setLeftDrawerOpen}>
              <SheetContent side="left" className="w-[85vw] max-w-sm p-0">
                <SheetTitle className="sr-only">Chats</SheetTitle>
                <SheetDescription className="sr-only">
                  Chat navigation drawer
                </SheetDescription>
                {sidebarContent}
              </SheetContent>
            </Sheet>
            <Sheet
              open={panel !== null}
              onOpenChange={(open) => {
                if (!open) closePanel()
              }}
            >
              <SheetContent side="right" className="w-[92vw] max-w-md p-0">
                <SheetTitle className="sr-only">
                  {shownPanel ? PANEL_LABELS[shownPanel] : 'Панель'}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Инструменты: калькуляторы и шаблоны
                </SheetDescription>
                {renderPanels(true)}
              </SheetContent>
            </Sheet>
          </>
        )}
      </SidebarProvider>
      <SettingsModal
        open={settingsModalOpen}
        onOpenChange={setSettingsModalOpen}
      />
    </ChatLayoutContext.Provider>
  )
}
